import { Box } from "@mui/material";
import type { AnswerComponent } from "api";
import {
  Controller,
  type Control,
  type FieldValues,
  type Path,
} from "react-hook-form";
import { DynamicFormRequiredLabel } from "./DynamicFormRequiredLabel";
import { DynamicFormErrorTooltip } from "./DynamicFormErrorTooltip";
import { DynamicFormSelectItem } from "./DynamicFormSelectItem";

type Props<T extends FieldValues> = {
  options: AnswerComponent[];
  control: Control<T, unknown, T>;
  name: Path<T>;
  required?: boolean;
  onAnswer?: () => void;
};

export const DynamicFormSelect = <T extends FieldValues>(props: Props<T>) => {
  return (
    <Box sx={{ mt: 1 }}>
      <Controller
        control={props.control}
        name={props.name}
        rules={{
          required: props.required ? "This field is required" : false,
        }}
        render={({ field, fieldState }) => {
          return (
            <>
              <DynamicFormRequiredLabel required={props.required} />
              <DynamicFormErrorTooltip fieldState={fieldState} />
              <Box
                sx={(theme) => ({
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  border: fieldState.error
                    ? `1px solid ${theme.palette.error.main}`
                    : `1px solid ${theme.palette.divider}`,
                  padding: 1,
                  borderRadius: 2,
                })}
                ref={field.ref}
                id={field.name}
              >
                {props.options.map((answer) => (
                  <DynamicFormSelectItem
                    key={answer.id}
                    answer={answer}
                    checked={field.value?.[0]?.id === answer.id}
                    disabled={field.disabled}
                    onChange={() => {
                      field.onChange([{ id: answer.id }]);
                      field.onBlur();
                      props.onAnswer?.();
                    }}
                  />
                ))}
              </Box>
            </>
          );
        }}
      />
    </Box>
  );
};
